import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Lock } from "lucide-react";
import { Fragment } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Card, PageHead, Spinner, Switch, cx, useToast } from "../../components/ui";

const DESCRIPTIONS: Partial<Record<S["StageName"], string>> = {
  imagery: "Satellite imagery, elevation and irradiance from Google Solar.",
  roof_outline: "The building’s roof outline (SAM2 segmentation).",
  roof_model: "Roof faces, pitches, edges and ridges in 3D.",
  obstructions: "Vents, chimneys and skylights kept free of modules.",
  setbacks: "Fire-code clearance along roof edges.",
  irradiance: "Sunlight per square metre on every face.",
  layout: "Module placement, best positions first.",
  quote: "Pricing, savings and financing.",
};

export function AdminStages() {
  const qc = useQueryClient();
  const toast = useToast();
  const stages = useQuery({ queryKey: ["stages"], queryFn: () => unwrap(api.GET("/v1/admin/stages")) });
  const update = useMutation({
    mutationFn: (disabled: S["StageName"][]) => unwrap(api.PUT("/v1/admin/stages", { body: { disabled_stages: disabled } })),
    onSuccess: (data) => {
      qc.setQueryData(["stages"], data);
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (stages.isLoading) return <Spinner />;
  if (stages.error || !stages.data) return <Alert tone="danger">{errorMessage(stages.error)}</Alert>;
  const items = stages.data.items;
  const disabled = items.filter((s) => !s.enabled).map((s) => s.stage);
  const toggle = (stage: S["StageSwitch"], on: boolean) => {
    const next = on ? disabled.filter((s) => s !== stage.stage) : [...disabled, stage.stage];
    update.mutate(next, { onSuccess: () => toast(`${stage.label} ${on ? "switched on" : "switched off"} for new jobs`) });
  };
  return (
    <>
      <PageHead title="Pipeline stages" description="Switch optional stages off platform-wide. New jobs skip them; the stages after still run. Running jobs are not affected." />
      <div className="stack">
        <Card pad={false}>
          <div className="card-body">
            <div className="pipeline-flow">
              {items.map((s, i) => (
                <Fragment key={s.stage}>
                  {i > 0 && <ChevronRight />}
                  <span className={cx("node", !s.enabled && "off")}>{s.label}</span>
                </Fragment>
              ))}
            </div>
          </div>
        </Card>
        <Card pad={false}>
          <div className="stage-list">
            {items.map((s, i) => (
              <div key={s.stage} className={cx("stage-item", s.optional && "optional", !s.enabled && "off")}>
                <div className="idx">{i + 1}</div>
                <div>
                  <div className="row" style={{ gap: 8 }}>
                    <b>{s.label}</b>
                    <code className="faint">{s.stage}</code>
                    {s.optional ? <Badge tone={s.enabled ? "ok" : "warn"}>{s.enabled ? "on" : "off"}</Badge> : null}
                  </div>
                  <div className="small muted" style={{ marginTop: 2 }}>
                    {DESCRIPTIONS[s.stage]}
                  </div>
                </div>
                {s.optional ? (
                  <Switch checked={s.enabled} disabled={update.isPending} onChange={(on) => toggle(s, on)} />
                ) : (
                  <span className="lock" title="Core stage: always runs">
                    <Lock /> Core
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
        <p className="hint">Stored as platform setting <code>disabled_stages</code>; changes are written to the audit trail.</p>
      </div>
    </>
  );
}
